import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * RiskInheritancePage — visualize cross-module risk inheritance.
 * BCM Manager / Dept Head only.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Network, RefreshCw, Zap, ArrowRight, Loader2, GitBranch } from "lucide-react";

export default function RiskInheritancePage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [graph, setGraph] = useState({ links: [], count: 0 });
  const [propagating, setPropagating] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/risk-inheritance/graph");
      setGraph(res.data);
    } catch (err) {
      toast.error("Failed to load inheritance graph");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const propagate = async () => {
    setPropagating(true);
    try {
      const res = await api.post("/risk-inheritance/propagate");
      setLastResult(res.data);
      toast.success(`Propagation complete — ${res.data.total_inherited} link(s) generated`);
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Propagation failed"));
    } finally {
      setPropagating(false);
    }
  };

  const byRule = graph.links.reduce((acc, l) => {
    acc[l.rule] = (acc[l.rule] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="risk-inheritance-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">Cross-Module Intelligence</Badge>
            <Badge variant="outline">ISO 22301 §8.2.3 + §8.3</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Risk Inheritance Graph</h1>
          <p className="text-slate-600 mt-1">
            Auto-propagates risk signals across BIA → Risk Assessment → Applications.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={propagate} disabled={propagating}
            className="bg-purple-700 hover:bg-purple-800" data-testid="propagate-btn">
            {propagating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Run Propagation
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-slate-500">Total inherited links</p>
          <p className="text-2xl font-bold">{graph.count}</p>
        </CardContent></Card>
        <Card className="bg-sky-50"><CardContent className="pt-4">
          <p className="text-xs text-sky-700">R1 · Critical Process → App</p>
          <p className="text-2xl font-bold text-sky-700">{byRule.R1 || 0}</p>
        </CardContent></Card>
        <Card className="bg-amber-50"><CardContent className="pt-4">
          <p className="text-xs text-amber-700">R2 · High Residual Upstream</p>
          <p className="text-2xl font-bold text-amber-700">{byRule.R2 || 0}</p>
        </CardContent></Card>
        <Card className="bg-emerald-50"><CardContent className="pt-4">
          <p className="text-xs text-emerald-700">Last propagation</p>
          <p className="text-sm font-semibold text-emerald-700">
            {lastResult ? "Just now" : (graph.links[0]?.created_at ? new Date(graph.links[0].created_at).toLocaleDateString() : "Never")}
          </p>
        </CardContent></Card>
      </div>

      {/* Inheritance Rules */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <GitBranch className="w-5 h-5" /> Active Inheritance Rules
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="p-3 bg-slate-50 rounded">
            <p className="font-semibold">R1 — Critical Process supports Application</p>
            <p className="text-xs text-slate-600 mt-1">
              BIA criticality <code>Tier 1</code> → flags dependent apps as <code>critical_process_supported</code>
            </p>
          </div>
          <div className="p-3 bg-slate-50 rounded">
            <p className="font-semibold">R2 — High Residual Risk propagates upstream</p>
            <p className="text-xs text-slate-600 mt-1">
              Approved RA with residual ≥ 15 → flags upstream processes as <code>high_residual_upstream</code> with inherited score = source × 0.75
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Links list */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Network className="w-5 h-5" /> Inheritance Links ({graph.count})
          </CardTitle>
          <CardDescription>
            Each link shows a source asset whose risk influences a target asset.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {graph.links.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No inheritance links yet. Click <strong>Run Propagation</strong> to analyze.
            </div>
          ) : (
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {graph.links.map((l) => (
                <div key={l.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`link-${l.id}`}>
                  <Badge className={l.rule === "R1" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}>
                    {l.rule}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="truncate">
                      <span className="font-medium">{l.source_name || l.source_id}</span>
                      <span className="text-slate-400 mx-2">({l.source_type})</span>
                      <ArrowRight className="w-3 h-3 inline mx-1 text-slate-400" />
                      <span className="font-medium">{l.target_name || l.target_id}</span>
                      <span className="text-slate-400 ml-2">({l.target_type})</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      flag: <code>{l.flag}</code>
                      {l.inherited_score && ` · inherited_score: ${l.inherited_score}`}
                      {l.source_residual && ` · source_residual: ${l.source_residual}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
